import { useState, useEffect, useCallback } from "react";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Users, Plus, Trash2, Edit2, Crown, GripVertical,
  ChevronDown, ChevronRight, X, Check, UserPlus, Search
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";

const API = "/api";

export default function TeamGroups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [gRes, uRes] = await Promise.all([
        authFetch(`${API}/employee-groups`),
        authFetch(`${API}/users`),
      ]);
      if (gRes.ok) setGroups(await gRes.json());
      if (uRes.ok) setUsers(await uRes.json());
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (groupId) => {
    if (!confirm("Gruppe wirklich loeschen?")) return;
    const res = await authFetch(`${API}/employee-groups/${groupId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Gruppe geloescht"); loadData(); }
    else toast.error("Fehler");
  };

  if (loading) return <div className="py-4 text-[12px] text-gray-400">Lade Gruppen...</div>;

  const isAdmin = user?.role === "admin" || user?.role === "projektleiter";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-600" />
          <h2 className="text-[15px] font-semibold text-gray-800">Teams & Gruppen</h2>
          <span className="text-[11px] text-gray-400">{groups.length} Gruppen</span>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5 bg-slate-800 hover:bg-slate-900 text-[12px] h-8" data-testid="create-group-btn">
            <Plus className="w-3.5 h-3.5" /> Gruppe erstellen
          </Button>
        )}
      </div>

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Noch keine Gruppen erstellt</p>
          <p className="text-xs text-gray-400 mt-1">Erstellen Sie Teams fuer schnelleres Sammelstempeln</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {groups.map((g) => {
            const isExpanded = expandedGroup === g.id;
            const leader = users.find(u => u.id === g.leader_id);
            return (
              <div key={g.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`group-card-${g.id}`}>
                {/* Group Header */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedGroup(isExpanded ? null : g.id)}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-gray-900">{g.name}</span>
                      {g.is_temporary && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Temporaer</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[11px] text-gray-400">{g.members?.length || 0} Mitglieder</span>
                      {leader && (
                        <span className="flex items-center gap-1 text-[11px] text-blue-600">
                          <Crown className="w-3 h-3" /> {leader.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Member avatars */}
                    <div className="flex -space-x-1.5">
                      {(g.members || []).slice(0, 4).map((m, i) => (
                        <div key={m.id} className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[9px] font-semibold text-slate-600" title={m.name}>
                          {m.name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                        </div>
                      ))}
                      {(g.members?.length || 0) > 4 && <span className="text-[10px] text-gray-400 ml-1">+{g.members.length - 4}</span>}
                    </div>
                    {isAdmin && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); setEditGroup(g); }} className="p-1 hover:bg-gray-100 rounded" data-testid={`edit-group-${g.id}`}>
                          <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(g.id); }} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-group-${g.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded: Member List */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 py-2 space-y-1 bg-gray-50/50">
                    {g.excluded_members?.length > 0 && (
                      <div className="flex items-center gap-1.5 px-2 py-1 mb-1">
                        <span className="text-[10px] text-amber-600 font-medium">{g.excluded_members.length} Mitglied(er) ausgeschlossen (z.B. krank)</span>
                      </div>
                    )}
                    {(g.members || []).map((m) => {
                      const fullUser = users.find(u => u.id === m.id);
                      const isLeader = m.id === g.leader_id;
                      const isExcluded = (g.excluded_members || []).includes(m.id);
                      return (
                        <div key={m.id} className={`flex items-center gap-3 py-1.5 px-2 rounded hover:bg-white transition-colors ${isExcluded ? "opacity-50" : ""}`} data-testid={`group-member-${m.id}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold ${isExcluded ? "bg-red-100 text-red-500" : "bg-slate-200 text-slate-600"}`}>
                            {m.name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-[12px] font-medium ${isExcluded ? "text-gray-400 line-through" : "text-gray-800"}`}>{m.name}</p>
                            {fullUser && <p className="text-[10px] text-gray-400">{fullUser.email}</p>}
                          </div>
                          {isLeader && (
                            <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                              <Crown className="w-3 h-3" /> Gruppenleiter
                            </span>
                          )}
                          {!isLeader && isAdmin && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const action = isExcluded ? "include" : "exclude";
                                const res = await authFetch(`${API}/employee-groups/${g.id}/${action}/${m.id}`, { method: "POST" });
                                if (res.ok) { toast.success(isExcluded ? `${m.name} wieder aktiv` : `${m.name} ausgeschlossen`); loadData(); }
                              }}
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${isExcluded ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-red-50 text-red-500 hover:bg-red-100"}`}
                              data-testid={`toggle-exclude-${m.id}`}
                              title={isExcluded ? "Wieder aktivieren" : "Ausschliessen (z.B. krank)"}
                            >
                              {isExcluded ? "Aktivieren" : "Ausschliessen"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      {(showCreate || editGroup) && (
        <GroupDialog
          group={editGroup}
          users={users}
          onClose={() => { setShowCreate(false); setEditGroup(null); }}
          onSaved={() => { setShowCreate(false); setEditGroup(null); loadData(); }}
        />
      )}
    </div>
  );
}

// ==================== GROUP CREATE/EDIT DIALOG ====================

function GroupDialog({ group, users, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [selectedIds, setSelectedIds] = useState(new Set(group?.member_ids || []));
  const [leaderId, setLeaderId] = useState(group?.leader_id || "");
  const [isTemporary, setIsTemporary] = useState(group?.is_temporary || false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const toggleMember = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
        if (leaderId === userId) setLeaderId("");
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name erforderlich"); return; }
    if (selectedIds.size === 0) { toast.error("Mindestens ein Mitglied"); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        member_ids: [...selectedIds],
        leader_id: leaderId || null,
        is_temporary: isTemporary,
      };
      const url = group ? `${API}/employee-groups/${group.id}` : `${API}/employee-groups`;
      const method = group ? "PUT" : "POST";
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success(group ? "Gruppe aktualisiert" : "Gruppe erstellt");
        onSaved();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler");
      }
    } catch { toast.error("Fehler"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <Users className="w-4 h-4" />
            {group ? "Gruppe bearbeiten" : "Neue Gruppe erstellen"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Group Name */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1 block">Gruppenname</label>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Team Leipzig, Montage 1"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="group-name-input"
            />
          </div>

          {/* Temporary Toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={isTemporary} onChange={(e) => setIsTemporary(e.target.checked)} className="rounded border-gray-300" />
            <span className="text-[12px] text-gray-600">Schnellgruppe (temporaer fuer den Tag)</span>
          </label>

          {/* Member Selection */}
          <div>
            <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1 block">Mitglieder ({selectedIds.size} gewaehlt)</label>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Mitarbeiter suchen..."
                className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid="group-member-search"
              />
            </div>
            <div className="border border-gray-200 rounded-lg max-h-[250px] overflow-y-auto divide-y divide-gray-100">
              {filteredUsers.map((u) => {
                const isSelected = selectedIds.has(u.id);
                const isLeader = u.id === leaderId;
                return (
                  <div
                    key={u.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${isSelected ? "bg-blue-50/70" : "hover:bg-gray-50"}`}
                    onClick={() => toggleMember(u.id)}
                    data-testid={`group-user-${u.id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? "bg-slate-800 border-slate-800" : "border-gray-300"}`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-600">
                      {u.name.split(" ").map(n => n[0]).join("").substring(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium text-gray-800">{u.name}</p>
                      <p className="text-[10px] text-gray-400">{u.email}</p>
                    </div>
                    {isSelected && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setLeaderId(isLeader ? "" : u.id); }}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${isLeader ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600"}`}
                        data-testid={`set-leader-${u.id}`}
                        title="Als Gruppenleiter festlegen"
                      >
                        <Crown className="w-3 h-3" />
                        {isLeader ? "Leiter" : "Leiter?"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Leader Info */}
          {leaderId && (
            <div className="bg-blue-50 rounded-lg px-3 py-2">
              <p className="text-[11px] text-blue-700">
                <Crown className="w-3 h-3 inline mr-1" />
                <strong>{users.find(u => u.id === leaderId)?.name}</strong> kann fuer die Gruppe Sammelstempeln
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-[12px]">Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-1.5 bg-slate-800 hover:bg-slate-900 text-[12px]" data-testid="group-save-btn">
            {saving ? "Speichere..." : group ? "Aktualisieren" : "Erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
